import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AddNewSectionDefinitionDto,
  AttributeDefinition,
  FormTypes,
  SectionDefinitionEntityType,
} from 'shared-lib';
import { SectionDefinitionService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-new-section-definition',
  templateUrl: './add-new-section-definition.component.html',
  styleUrl: './add-new-section-definition.component.scss',
  standalone: false,
})
export class AddNewSectionDefinitionComponent implements OnInit {
  @Output() closeModal = new EventEmitter<void>();
  sectionDefinition: AddNewSectionDefinitionDto =
    new AddNewSectionDefinitionDto();

  addNewFieldOpened = false;
  activeDropdownIndex: number | null = null;
  FormTypes = FormTypes;

  @Output() saved = new EventEmitter<void>();

  entityTypes: SectionDefinitionEntityType[] = Object.values(
    SectionDefinitionEntityType
  ).filter((v) => typeof v === 'number') as SectionDefinitionEntityType[];

  selectedEntityType: SectionDefinitionEntityType;

  entityTypeLabels: Partial<Record<SectionDefinitionEntityType, string>> = {
    [SectionDefinitionEntityType.Pole]: 'Pole',
    [SectionDefinitionEntityType.DistributionSubstation]:
      'Distribution Substation',
    [SectionDefinitionEntityType.InjectionSubstation]: 'Injection Substation',
    [SectionDefinitionEntityType.TransmissionStation]: 'Transmission Station',
  };

  constructor(
    private sectionDefinitionService: SectionDefinitionService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (
      !this.sectionDefinition.attributeDefinitions ||
      this.sectionDefinition.attributeDefinitions.length === 0
    ) {
      this.sectionDefinition.attributeDefinitions = [
        this.getEmptyAttributeDefinition(),
      ];
    }
  }

  onCancel() {
    this.closeModal.emit();
  }

  onBackdropClick() {
    this.closeModal.emit();
  }

  onSave() {
    if (!this.validate()) {
      return;
    }

    this.sectionDefinition.assetType = this.selectedEntityType ?? undefined;

    this.sectionDefinitionService.save(this.sectionDefinition).subscribe(
      (data) => {
        this.toastr.success('Equipment successfully added');
        this.saved.emit();
        this.closeModal.emit();
      },
      (error) => {
        this.toastr.error(error);
      }
    );
  }

  validate() {
    if (!this.sectionDefinition.name) {
      this.toastr.error('Device name is missing!');
      return false;
    }

    if (
      !this.sectionDefinition.attributeDefinitions ||
      this.sectionDefinition.attributeDefinitions.length === 0
    ) {
      this.toastr.error('At least one attribute is required!');
      return false;
    }

    for (
      let i = 0;
      i < this.sectionDefinition.attributeDefinitions.length;
      i++
    ) {
      const attribute = this.sectionDefinition.attributeDefinitions[i];

      if (attribute.fieldType === FormTypes.Dropdown) {
        const optionValues = attribute.optionDefinitions
          .map((opt) => opt.value?.trim())
          .filter((v) => v !== undefined);

        const hasEmpty = optionValues.some((v) => !v);
        if (hasEmpty) {
          this.toastr.error(
            `Attribute "${attribute.label || 'Unnamed'}" has empty option(s).`
          );
          return false;
        }

        if (optionValues.length < 2) {
          this.toastr.error(
            `Attribute "${
              attribute.label || 'Unnamed'
            }" must have at least 2 options.`
          );
          return false;
        }

        const seen = new Set<string>();
        const duplicates: string[] = [];
        for (const val of optionValues) {
          const lower = val.toLowerCase();
          if (seen.has(lower)) {
            duplicates.push(val);
          } else {
            seen.add(lower);
          }
        }

        if (duplicates.length > 0) {
          this.toastr.error(
            `Attribute "${
              attribute.label || 'Unnamed'
            }" has duplicate values: ${[...new Set(duplicates)].join(', ')}`
          );
          return false;
        }
      }
    }

    if (this.selectedEntityType == null) {
      this.toastr.error('Asset type is empty!');
      return false;
    }
    return true;
  }

  onAddField() {
    this.addNewFieldOpened = true;
  }

  onCloseAddNewFieldModal() {
    this.addNewFieldOpened = false;
  }

  toggleAsset(type: SectionDefinitionEntityType) {
    this.selectedEntityType = type;
  }

  addOption(attributeDefinition: AttributeDefinition) {
    attributeDefinition.optionDefinitions.push({
      id: 0,
      value: '',
      displayOrder: attributeDefinition.optionDefinitions.length + 1,
      parentOptionId: 0,
    });
  }

  drop(event: CdkDragDrop<string[]>, optionDefinitions: any[]) {
    moveItemInArray(optionDefinitions, event.previousIndex, event.currentIndex);

    optionDefinitions.forEach((option, index) => {
      option.order = index;
    });
  }

  getEmptyAttributeDefinition(): AttributeDefinition {
    return {
      id: 0,
      label: 'Device Type',
      placeholderText: '',
      fieldType: FormTypes.Dropdown,
      isRequired: true,
      defaultState: true,
      minNumberValue: undefined,
      maxNumberValue: undefined,
      displayOrder: 1,
      is24HoursFormat: undefined,
      optionDefinitions: [
        { id: 0, value: '', displayOrder: 1, parentOptionId: 0 },
        { id: 0, value: '', displayOrder: 2, parentOptionId: 0 },
        { id: 0, value: '', displayOrder: 3, parentOptionId: 0 },
      ],
      parentAttributeId: 0,
      guid: crypto.randomUUID(),
    };
  }

  toggleDropdown(index: number): void {
    this.activeDropdownIndex =
      this.activeDropdownIndex === index ? null : index;
  }

  deleteOption(attributeDefinition: any, index: number): void {
    attributeDefinition.optionDefinitions.splice(index, 1);
    this.activeDropdownIndex = null;
  }
}
